// client/utils/ontologyFilters.ts
// Filter utility for ontology graph with node-link consistency

interface NodeMeta {
  file?: string;
  _provider?: string;
  [key: string]: unknown;
}

interface LinkMeta {
  provider?: string;
  matchType?: string;
  [key: string]: unknown;
}

type Graph = {
  nodes: Array<{ id: string; name: string; kind: string; meta?: NodeMeta }>;
  links: Array<{ source: string; target: string; value: number; meta?: LinkMeta }>;
};

export type OntologyFilters = {
  provider: 'all' | 'local' | 'ols' | 'bioportal';
  matchType: 'all' | 'Exact' | 'Fuzzy' | 'None';
};

/**
 * Apply filters to ontology graph
 * Filters links first, then rebuilds node set to maintain consistency
 *
 * @param graph - Original graph with nodes and links
 * @param filters - Filter criteria for provider and matchType
 * @returns Filtered graph with no orphan nodes
 */
export function applyFilters(graph: Graph, filters: OntologyFilters): Graph {
  const providerOk = (p?: string) => filters.provider === 'all' || p === filters.provider;
  const mtOk = (m?: string) => filters.matchType === 'all' || m === filters.matchType;

  // Filter links by provider and matchType
  const links = graph.links.filter(l => providerOk(l.meta?.provider) && mtOk(l.meta?.matchType));

  // Keep only nodes touched by filtered links
  const keep = new Set<string>();
  links.forEach(l => {
    keep.add(String(l.source));
    keep.add(String(l.target));
  });

  const nodes = graph.nodes.filter(n => keep.has(n.id));

  return { nodes, links };
}
